import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
// Icons
import { FaChevronDown } from 'react-icons/fa6';
import { IoMdMenu } from 'react-icons/io';
// Images
import MainLogo from '../../assets/images/logos/miss-dominatrixa-logo-monotone.webp';
import PhoneLogo from '../../assets/images/logos/miss-dominatrixa-logo-monotone-small.webp';
// Nav data
import { getNavLinkItemsArray } from '../../utils/data/NavData';
// Constants
import { CompanyName } from '../../utils/Constants';
// Context
import { useUser } from '../../context/UserContext';
import { HOME_PAGE_URL } from '../../utils/Routes';


function Navbar({ isHidden }) {
  const { user, logout } = useUser();
  const navLinkItemsArray = getNavLinkItemsArray(user);

  const [isPhoneNavOpen, setIsPhoneNavOpen] = useState(false);
  const [isAdminNavOpen, setIsAdminNavOpen] = useState(false);
  const [isSessionsOpen, setIsSessionsOpen] = useState(false);

  const togglePhoneNav = () => setIsPhoneNavOpen((prev) => !prev);
  const toggleSessionsMenu = () => {
    setIsSessionsOpen((prev) => {
      if (!prev) setIsAdminNavOpen(false); // close Admin if opening Sessions
      return !prev;
    });
  };

  const toggleAdminMenu = () => {
    setIsAdminNavOpen((prev) => {
      if (!prev) setIsSessionsOpen(false); // close Sessions if opening Admin
      return !prev;
    });
  };

  return (
    <nav
      className={`relative lg:fixed lg:w-full transition-opacity duration-100 ${
        isHidden ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      <section className='grid gap-2 lg:gap-4 pb-4 pt-8 lg:pt-12 md:pt-10 lg:py-6 leading-5'>
        {/* Logo */}
        <section className='grid justify-center pt-2 sm:pt-3 md:pt-4 lg:pt-0 px-2'>
          <NavLink to={HOME_PAGE_URL} className='px-4'>
            {/* Desktop Logo */}
            <img
              src={MainLogo}
              alt={CompanyName}
              className='hidden md:block border-b-4 border-solid border-black pb-2'
            />
            {/* Mobile Logo */}
            <img
              src={PhoneLogo}
              alt={CompanyName}
              className='block md:hidden px-2 '
            />
          </NavLink>
        </section>

        {/* Navigation */}
        <section className='grid relative md:justify-center '>
          {/* Mobile Navigation */}
          {isPhoneNavOpen && (
            <section className='absolute top-full left-0 w-full bg-colour3 border-b-2 border-t-2 border-solid border-colour2 full_shadow_black_invert transition-transform duration-300 '>
              <ul className='grid gap-4 text-center py-6 shadow-md shadow-colour2/50 no__highlights'>
                {navLinkItemsArray.map(({ path, label, subpages }) =>
                  subpages ? (
                    <li key={label}>
                      <div
                        className='flex justify-center gap-4 hover:brightness-90 duration-200'
                        onClick={() => {
                          if (label === 'Sessions') toggleSessionsMenu();
                          else if (label === 'Admin') toggleAdminMenu();
                        }}
                      >
                        <span className='text-xl md:text-base text-colour5 font-medium font-titleFont no__highlights'>
                          {label}
                        </span>
                        <span
                          className={`flex transition-transform duration-300 text-colour5 ${
                            isSessionsOpen ? 'rotate-180 mb-1.5' : 'mt-1'
                          }`}
                        >
                          <FaChevronDown />
                        </span>
                      </div>
                      {/* Sessions submenu */}
                      {label === 'Sessions' && isSessionsOpen && (
                        <ul className='grid gap-3 mt-2 bg-colour4 full_shadow_black_invert py-4 border-b-2 border-t-2 border-solid border-colour2 no__highlights'>
                          {subpages.map(({ path, label }) => (
                            <NavItem key={label} url={path} title={label} />
                          ))}
                        </ul>
                      )}

                      {/* Admin submenu */}
                      {label === 'Admin' && isAdminNavOpen && (
                        <ul className='grid gap-3 mt-2 bg-colour4 full_shadow_black_invert py-4 border-b-2 border-t-2 border-solid border-colour2 no__highlights'>
                          {subpages.map(({ path, label }) => (
                            <NavItem key={label} url={path} title={label} />
                          ))}
                        </ul>
                      )}
                    </li>
                  ) : (
                    <NavItem key={label} url={path} title={label} />
                  )
                )}
                {user?.id && (
                  <li
                    onClick={logout}
                    className={`text-xl md:text-xs lg:text-sm font-medium font-titleFont hover:brightness-90 duration-200 active:scale-75 text-colour5 no__highlights`}
                  >
                    Logout
                  </li>
                )}
              </ul>
            </section>
          )}

          {/* Mobile Menu Button */}
          <section className='grid md:hidden items-center w-full px-8 sm:px-16 mt-2'>
            <div className='grid grid-cols-rev gap-1 items-center border-colour5 border-solid border-2 w-full rounded-lg px-2'>
              <div className='text-colour5 bg-colour3 rounded pl-2'>
                {(() => {
                  const currentPath = window.location.pathname;

                  // Get all paths from the nav array, including subpages
                  const navItems = getNavLinkItemsArray().flatMap(
                    ({ path, label, subpages }) => [
                      ...(path ? [{ path, label }] : []), // Include main links
                      ...(subpages
                        ? subpages.map(({ path, label }) => ({ path, label }))
                        : []), // Include subpages
                    ]
                  );

                  // Find the corresponding label for the current path
                  const matchingNavItem = navItems.find(
                    ({ path }) => path === currentPath
                  );

                  return matchingNavItem?.label || 'Page Not Found'; // Use label if found, else fallback
                })()}
              </div>
              <button
                onClick={togglePhoneNav}
                className='grid w-fit h-fit items-center justify-center text-4xl text-colour5 cursor-pointer'
              >
                <IoMdMenu className='active:scale-90 duration-300' />
              </button>
            </div>
          </section>

          {/* Large Screen Navigation */}
          <ul className='hidden md:flex gap-4 lg:gap-7 w-fit mx-auto items-center'>
            {navLinkItemsArray.map(({ path, label, subpages }) =>
              subpages ? (
                <li key={label} className='relative group'>
                  <span className='text-xl no__highlights text-colour5 cursor-pointer md:text-xs lg:text-sm font-medium font-titleFont hover:brightness-90 duration-200 active:scale-75'>
                    {label}
                  </span>
                  <ul className='absolute left-0 top-full mt-2 bg-colour5 top_shadow border-2 border-solid border-colour4 shadow-cardShadowBold rounded-md p-3 grid gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 min-w-[220px]'>
                    {subpages.map(({ path, label }) => (
                      <div
                        key={label}
                        className='border-b-[1px] border-solid border-colour2'
                      >
                        <NavItem
                          url={path}
                          title={label}
                          className='!text-colour3'
                        />
                      </div>
                    ))}
                  </ul>
                </li>
              ) : (
                <NavItem key={label} url={path} title={label} />
              )
            )}
            {user?.id && (
              <li
                onClick={logout}
                className={`text-xl md:text-xs lg:text-sm font-medium font-titleFont hover:brightness-90 duration-200 active:scale-75 text-colour5 no__highlights`}
              >
                Logout
              </li>
            )}
          </ul>
        </section>
      </section>
    </nav>
  );
}

const NavItem = ({ url, title, className = '' }) => (
  <li className='active:scale-90'>
    <NavLink
      to={url}
      className={`text-xl md:text-xs lg:text-sm font-medium font-titleFont hover:brightness-90 duration-200 active:scale-75 text-colour5 no__highlights ${className}`}
      aria-label={`${title} page navigation tab`}
    >
      {title}
    </NavLink>
  </li>
);

export default Navbar;
